import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const SetoresChart = () => {
  const setores = ["Deck", "Salao", "PeNaAreia", "Piscina", "Lounge"];

  const [chamadasPendentes, setChamadasPendentes] = useState([0, 0, 0, 0, 0]);
  const [chamadasAndamento, setChamadasAndamento] = useState([0, 0, 0, 0, 0]);
  const [chamadasFinalizadas, setChamadasFinalizadas] = useState([0, 0, 0, 0, 0]);

  const [contasPendentes, setContasPendentes] = useState([0, 0, 0, 0, 0]);
  const [contasAndamento, setContasAndamento] = useState([0, 0, 0, 0, 0]);
  const [contasFinalizadas, setContasFinalizadas] = useState([0, 0, 0, 0, 0]);

  const [totalChamadas, setTotalChamadas] = useState(0);

  const [dataInicio, setDataInicio] = useState(() => new Date().toISOString().split("T")[0]);
  const [dataFim, setDataFim] = useState(() => new Date().toISOString().split("T")[0]);

  const chartRef = useRef(null);

  useEffect(() => {
    if (!dataInicio || !dataFim) return;

    const q = query(
      collection(db, "chamadas"),
      where("data_hora", ">=", `${dataInicio} 00:00:00`),
      where("data_hora", "<=", `${dataFim} 23:59:59`)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const cont = {
        chamadasPendentes: { Deck: 0, Salao: 0, PeNaAreia: 0, Piscina: 0, Lounge: 0 },
        chamadasAndamento: { Deck: 0, Salao: 0, PeNaAreia: 0, Piscina: 0, Lounge: 0 },
        chamadasFinalizadas: { Deck: 0, Salao: 0, PeNaAreia: 0, Piscina: 0, Lounge: 0 },
        contasPendentes: { Deck: 0, Salao: 0, PeNaAreia: 0, Piscina: 0, Lounge: 0 },
        contasAndamento: { Deck: 0, Salao: 0, PeNaAreia: 0, Piscina: 0, Lounge: 0 },
        contasFinalizadas: { Deck: 0, Salao: 0, PeNaAreia: 0, Piscina: 0, Lounge: 0 },
      };

      snapshot.forEach((d) => {
        const data = d.data();
        const { setor, tipo, status } = data;
        if (!setores.includes(setor)) return;

        if (tipo === "chamada" && status === "pendente") cont.chamadasPendentes[setor]++;
        if (tipo === "chamada" && status === "em_andamento") cont.chamadasAndamento[setor]++;
        if (tipo === "chamada" && status === "finalizado") cont.chamadasFinalizadas[setor]++;

        if (tipo === "conta" && status === "pendente") cont.contasPendentes[setor]++;
        if (tipo === "conta" && status === "em_andamento") cont.contasAndamento[setor]++;
        if (tipo === "conta" && status === "finalizado") cont.contasFinalizadas[setor]++;
      });

      setChamadasPendentes(setores.map((s) => cont.chamadasPendentes[s]));
      setChamadasAndamento(setores.map((s) => cont.chamadasAndamento[s]));
      setChamadasFinalizadas(setores.map((s) => cont.chamadasFinalizadas[s]));
      setContasPendentes(setores.map((s) => cont.contasPendentes[s]));
      setContasAndamento(setores.map((s) => cont.contasAndamento[s]));
      setContasFinalizadas(setores.map((s) => cont.contasFinalizadas[s]));

      const total = setores.reduce(
        (acc, s) =>
          acc +
          cont.chamadasPendentes[s] +
          cont.chamadasAndamento[s] +
          cont.chamadasFinalizadas[s] +
          cont.contasPendentes[s] +
          cont.contasAndamento[s] +
          cont.contasFinalizadas[s],
        0
      );
      setTotalChamadas(total);
    });

    return () => unsubscribe();
  }, [dataInicio, dataFim]);

  // paleta moderna
  const palette = useMemo(
    () => ({
      contasPendentes: { bg: "#ef4444", border: "#ef4444", alpha: 0.28 },
      chamadasPendentes: { bg: "#f59e0b", border: "#f59e0b", alpha: 0.28 },
      contasAndamento: { bg: "#22c55e", border: "#22c55e", alpha: 0.28 },
      chamadasAndamento: { bg: "#06b6d4", border: "#06b6d4", alpha: 0.28 },
      contasFinalizadas: { bg: "#8b5cf6", border: "#8b5cf6", alpha: 0.28 },
      chamadasFinalizadas: { bg: "#3b82f6", border: "#3b82f6", alpha: 0.28 },
    }),
    []
  );

  const toRGBA = (hex, alpha) => {
    const h = hex.replace("#", "");
    const n = parseInt(h, 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // headroom automático para os rótulos
  const maxValor = useMemo(() => {
    const todos = [
      ...contasPendentes, ...chamadasPendentes,
      ...contasAndamento, ...chamadasAndamento,
      ...contasFinalizadas, ...chamadasFinalizadas,
    ];
    return Math.max(0, ...todos);
  }, [
    contasPendentes, chamadasPendentes,
    contasAndamento, chamadasAndamento,
    contasFinalizadas, chamadasFinalizadas
  ]);

  const suggestedMax = useMemo(() => Math.max(1, Math.ceil(maxValor * 1.15)), [maxValor]);

  const data = useMemo(
    () => ({
      labels: setores,
      datasets: [
        {
          label: "Contas Pendentes",
          data: contasPendentes,
          backgroundColor: toRGBA(palette.contasPendentes.bg, palette.contasPendentes.alpha),
          borderColor: palette.contasPendentes.border,
          borderWidth: 1.5,
          borderRadius: 8,
          maxBarThickness: 36,
        },
        {
          label: "Chamadas Pendentes",
          data: chamadasPendentes,
          backgroundColor: toRGBA(palette.chamadasPendentes.bg, palette.chamadasPendentes.alpha),
          borderColor: palette.chamadasPendentes.border,
          borderWidth: 1.5,
          borderRadius: 8,
          maxBarThickness: 36,
        },
        {
          label: "Contas em Andamento",
          data: contasAndamento,
          backgroundColor: toRGBA(palette.contasAndamento.bg, palette.contasAndamento.alpha),
          borderColor: palette.contasAndamento.border,
          borderWidth: 1.5,
          borderRadius: 8,
          maxBarThickness: 36,
        },
        {
          label: "Chamadas em Andamento",
          data: chamadasAndamento,
          backgroundColor: toRGBA(palette.chamadasAndamento.bg, palette.chamadasAndamento.alpha),
          borderColor: palette.chamadasAndamento.border,
          borderWidth: 1.5,
          borderRadius: 8,
          maxBarThickness: 36,
        },
        {
          label: "Contas Finalizadas",
          data: contasFinalizadas,
          backgroundColor: toRGBA(palette.contasFinalizadas.bg, palette.contasFinalizadas.alpha),
          borderColor: palette.contasFinalizadas.border,
          borderWidth: 1.5,
          borderRadius: 8,
          maxBarThickness: 36,
        },
        {
          label: "Chamadas Finalizadas",
          data: chamadasFinalizadas,
          backgroundColor: toRGBA(palette.chamadasFinalizadas.bg, palette.chamadasFinalizadas.alpha),
          borderColor: palette.chamadasFinalizadas.border,
          borderWidth: 1.5,
          borderRadius: 8,
          maxBarThickness: 36,
        },
      ],
    }),
    [
      setores,
      contasPendentes, chamadasPendentes,
      contasAndamento, chamadasAndamento,
      contasFinalizadas, chamadasFinalizadas,
      palette
    ]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 24, right: 8, bottom: 8, left: 8 } }, // mais espaço no topo
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            padding: 20,
            color: "#111827",
            font: { size: 13, weight: "600" },
          },
        },
        title: {
          display: true,
          text: "Chamadas e Contas por Setor",
          color: "#0f172a",
          font: { size: 18, weight: "700" },
          padding: { top: 6, bottom: 6 },
        },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.9)",
          titleColor: "#fff",
          bodyColor: "#e5e7eb",
          borderColor: "rgba(255,255,255,0.1)",
          borderWidth: 1,
          padding: 10,
          callbacks: {
            footer: (items) => {
              const sum = items.reduce((acc, it) => acc + (it.parsed?.y || 0), 0);
              return `Total nesse setor: ${sum}`;
            },
          },
          footerFont: { weight: "600" },
        },
        datalabels: {
          anchor: "end",
          align: "end",
          offset: 6,
          color: "#111827",
          font: { weight: "700", size: 11 },
          formatter: (v) => (v > 0 ? v : ""),
          clip: false,   // não corta fora do chart
          clamp: true,   // “gruda” caso passe do topo
        },
      },
      scales: {
        x: {
          ticks: { color: "#334155", font: { size: 12, weight: "600" } },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          suggestedMax, // folga automática
          ticks: { color: "#475569", font: { size: 12 } },
          grid: { color: "rgba(148, 163, 184, 0.18)" },
        },
      },
      animation: { duration: 600, easing: "easeOutQuart" },
    }),
    [suggestedMax]
  );

  const handleDownloadPNG = () => {
    const chart = chartRef.current;
    if (!chart) return;
    const url = chart.toBase64Image("image/png", 1);
    const a = document.createElement("a");
    a.href = url;
    a.download = `setores_${dataInicio}_a_${dataFim}.png`;
    a.click();
  };

  return (
    <div style={{ maxWidth: "100%", padding: 12 }}>
      {/* Header / filtros */}
      <div
        className="shadow-sm"
        style={{
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          border: "1px solid #e2e8f0",
          borderRadius: 16,
          padding: 16,
          marginBottom: 12,
        }}
      >
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between" style={{ gap: 12 }}>
          <div>
            <div className="fw-bold" style={{ fontSize: 18, color: "#0f172a" }}>
              Painel por Setor
            </div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Período selecionado
            </div>
          </div>

          <div className="d-flex flex-column flex-md-row" style={{ gap: 8 }}>
            <div>
              <label className="form-label mb-1">Data Início</label>
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="form-control" />
            </div>
            <div>
              <label className="form-label mb-1">Data Fim</label>
              <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="form-control" />
            </div>
          </div>

          <div className="text-center">
            <div className="badge" style={{ backgroundColor: "#111827", color: "#fff", fontSize: 13, padding: "10px 12px", borderRadius: 999 }}>
              Total no período: <span className="fw-bold ms-1">{totalChamadas}</span>
            </div>
            <div className="mt-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={handleDownloadPNG}>
                Exportar PNG
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Chart card */}
      <div
        className="shadow-sm"
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 16,
          padding: 16,
        }}
      >
        <div style={{ width: "100%", height: 480 }}>
          <Bar ref={chartRef} data={data} options={options} />
        </div>

        {/* legenda rápida abaixo */}
        <div className="mt-3 d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
          <span className="text-muted" style={{ fontSize: 12 }}>Legenda rápida:</span>
          <span className="badge" style={{ background: toRGBA(palette.contasPendentes.bg, 0.2), color: palette.contasPendentes.border }}>Contas Pendentes</span>
          <span className="badge" style={{ background: toRGBA(palette.chamadasPendentes.bg, 0.2), color: palette.chamadasPendentes.border }}>Chamadas Pendentes</span>
          <span className="badge" style={{ background: toRGBA(palette.contasAndamento.bg, 0.2), color: palette.contasAndamento.border }}>Contas em Andamento</span>
          <span className="badge" style={{ background: toRGBA(palette.chamadasAndamento.bg, 0.2), color: palette.chamadasAndamento.border }}>Chamadas em Andamento</span>
          <span className="badge" style={{ background: toRGBA(palette.contasFinalizadas.bg, 0.2), color: palette.contasFinalizadas.border }}>Contas Finalizadas</span>
          <span className="badge" style={{ background: toRGBA(palette.chamadasFinalizadas.bg, 0.2), color: palette.chamadasFinalizadas.border }}>Chamadas Finalizadas</span>
        </div>
      </div>
    </div>
  );
};

export default SetoresChart;
